import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Row, Col, Typography, Button, Space, Steps } from 'antd';

import { isEmpty } from 'lodash';

import {
  CheckCircleFilled,
  LoadingOutlined,
  CloseCircleFilled,
  CreditCardFilled,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  FileOutlined,
} from '@ant-design/icons';

import {
  StyledModal,
  FormSaveButton,
  FormButton,
  WithLoading,
  dialogFunction,
} from 'common/components';

import { useQueryClient } from '@tanstack/react-query';

import * as productFullViewSelectors from 'pages/product-full-view/controllers/selectors';
import * as productFullViewActions from 'pages/product-full-view/controllers/actions';

import {
  productFullViewKeys,
  useGetProductFullView,
  useVisibleModal,
} from 'hooks';

import {
  useMutateMakeProductActive,
  useValidateActiveProduct,
  validateActiveProductKey,
} from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';

import { mappingGridProperties as groupPropertiesByModuleName } from 'pages/home/ribbon/components/sections/customize-grid/utils';

import { getPropertiesParams } from 'utils/minimumRequirementUtils';
import {
  IXOneGrid,
  IXOneHeader,
  IXOneMessage,
  IXOneTotalFee,
  RIVIRBillingReview,
  useIXOneColumns,
} from 'pages/home/ribbon/components/sections/product/components/ixone-credit-card';

import { AdditionalRequiredActiveProduct } from './AdditionalRequiredActiveProduct';
import { RequiredPropertyInput } from './RequiredPropertyInput';

import {
  RequiredModuleName,
  RequiredPropertiesForm,
  RequiredPropertiesSection,
  RequiredPropertyItem,
  RequiredPropertyName,
} from './RequiredPropertyElement';

import {
  COD_PAYMENT_DESCRIPTION,
  COD_PAYMENT_TYPE_VIEW,
  CODChargeValidating,
} from 'pages/home/ribbon/components/sections/product/components/cod-payment';

import { ContentConfirmRequest } from 'pages/home/ribbon/components/sections/product/components/ixone-section/IXOneCapture';

import {
  RequiredActiveProductContext,
  useCalculateFeeActiveProduct,
  useRequiredActiveProductContext,
  useSetFormValuesAndErrors,
  useUpdateUnmatchedRequirement,
} from './hooks';

import {
  calculateTotalFee,
  filteredColumnsByFee,
  filterInvalidValuesFormValues,
  getPassedProducts,
} from './utils';
import { lowercaseFirstChar } from 'hooks/useValidateSelfLoadedProduct';
import { sleep } from 'utils/delay';

import { useGetRequirementsForActiveProduct } from 'hooks/entityHooks';
import {
  useCheckPaymentTerm,
  usePayNowSavedMemberCard,
} from 'pages/home/ribbon/components/sections/product/components/cod-payment/hooks';

import {
  STEP_CHARGE,
  STEP_REQUIREMENT,
  additionalRequirementFieldsLowercase,
} from './constants';
import { keys } from 'pages/company-profile/components/tabs/billing/queries';
import { useQueryMarketingAssets } from 'pages/product-full-view/queries';
import { useGetSavedMemberCard } from 'pages/company-profile/components/tabs/billing/hooks';

import { getErrorsForActiveProduct } from 'pages/home/ribbon/components/sections/product/components/make-product-active/utils';
import { transformChargeItemsForCOD } from 'pages/home/ribbon/components/sections/product/components/cod-payment/utils';

import { useCheckPermissions } from 'hooks/useCheckPermissions';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import './RequiredActiveProduct.less';

const { Step } = Steps;

export const RequiredActiveProductRoot = ({ children }) => {
  const [requiredForm] = Form.useForm();

  const [currentStep, setCurrentStep] = useState(STEP_REQUIREMENT);

  const [isSubmitted, setIsSubmitted] = useState(false);

  return (
    <RequiredActiveProductContext.Provider
      value={{
        requiredForm,
        currentStep,
        setCurrentStep,
        setIsSubmitted,
        isSubmitted,
      }}
    >
      {children}
    </RequiredActiveProductContext.Provider>
  );
};

export const RequiredActiveProductModal = ({
  entityId,
  entityType,
  validateProducts,
  children,
  onCancel,
  productForm,
  onMakeActiveSuccessCallback = () => {},
  onRevalidateProduct = () => {},
  onUpdateUnmatchedSuccessCallback = () => {},
  ...otherProps
}) => {
  const { requiredForm, currentStep, setCurrentStep, setIsSubmitted } =
    useRequiredActiveProductContext();

  const unmatchedRequirementMutation = useUpdateUnmatchedRequirement();

  const hasBypassProductInternalPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  const totalFee = useCalculateFeeActiveProduct({
    productIds: [entityId],
  });

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const productHierarchyInfo = useSelector(
    productFullViewSelectors.makeSelectCurrentHierarchy()
  );

  const { validateProducts: initialValidateProducts } =
    useValidateActiveProduct({
      productIds: [entityId],
    });

  const isMatched = Boolean(validateProducts?.[0]?.activeResult?.isSuccess);

  const { productFull } = useGetProductFullView({
    productId: entityId,
  });

  // get meta data for product (fee, packageLevel)
  const passedProducts = getPassedProducts(
    validateProducts,
    initialValidateProducts
  );

  const activeRequiredFields =
    validateProducts?.[0]?.activeResult?.activeRequiredFields ?? [];

  const requiredErrorFields = getErrorsForActiveProduct(
    validateProducts?.[0]?.activeResult
  );

  const isEmptyEntityRequirement = isEmpty(requiredErrorFields);

  const [
    visibleReviewBilling,
    { offModal: offModalReviewBilling, onModal: onModalReviewBilling },
  ] = useVisibleModal();

  const { isCODTerm, isInvoicedTerm } = useCheckPaymentTerm();

  const canBypassCharge = hasBypassProductInternalPermission || !totalFee;

  const activeProductChargeMutation = useMutateMakeProductActive();

  const { invalidateMarketingAssets } = useQueryMarketingAssets({
    enabled: false,
  });
  const { hasCreditCard } = useGetSavedMemberCard();

  const codPayNow = usePayNowSavedMemberCard({ onCancel });

  const handleCODPayNow = async () => {
    let codParams = {
      totalAmount: totalFee,
      isSendToCapture: false,
      chargeItems: transformChargeItemsForCOD(passedProducts),
      chargeDescription:
        COD_PAYMENT_DESCRIPTION[COD_PAYMENT_TYPE_VIEW.activeProduct],
    };

    await codPayNow.mutateAsync(codParams);
  };

  const isRequirementStep = currentStep === 0;

  const confirmRequest = async () => {
    const params = {
      productIds: [entityId],
    };

    await activeProductChargeMutation.mutateAsync(params, {
      onSuccess: async (response) => {
        if (response?.isSuccess) {
          await handleUpdateUnmatchedRequirement({
            status: 'Active', // when make active product successfully, we need to update status = active
          });
          if (
            totalFee === 0 &&
            hasCreditCard &&
            isCODTerm &&
            !hasBypassProductInternalPermission
          )
            handleCODPayNow();
          await sleep();
          queryClient.invalidateQueries({
            queryKey: keys.allMemberBillingStatement(),
          });
          onMakeActiveSuccessCallback();
          onCancel();
        }
      },
    });
  };

  /**
   * Collects form values from both product form and required form.
   */
  const collectFormValues = () => {
    const productValuesFromModal = productForm?.getFieldsValue() ?? {};
    const requiredFormValues = requiredForm?.getFieldsValue() ?? {};

    let formValues = {
      ...productValuesFromModal,
      ...requiredFormValues,
    };

    // Remove invalid values
    formValues = filterInvalidValuesFormValues(formValues);

    // Normalize brickCode field
    formValues = {
      ...formValues,
      brickCode: formValues?.brickCode || formValues?.brick,
    };

    return formValues;
  };

  const filterRequiredFields = (formValues, mappingActiveRequiredFields) => {
    return mappingActiveRequiredFields.reduce((acc, field) => {
      if (field in formValues) {
        acc[field] = formValues[field];
      }
      return acc;
    }, {});
  };

  const buildPayload = (formValues, filteredFormValues, status) => {
    return {
      ...formValues,
      ...filteredFormValues,
      productId: formValues?.productId || formValues?.id || entityId,
      isVariableWeightsAndMeasures:
        formValues?.isVariableWeightsAndMeasures ??
        productFull?.isVariableWeightsAndMeasures ??
        false,
      packageLevel: formValues?.packageLevel,
      status: status ?? formValues?.status,
    };
  };

  /**
   * Updates unmatched requirement fields for the product.
   * This function collects form values, filters required fields, and submits them to update the product.
   */
  const handleUpdateUnmatchedRequirement = async ({ status } = {}) => {
    // Convert active required fields to lowercase for matching
    const mappingActiveRequiredFields = activeRequiredFields.map((field) =>
      lowercaseFirstChar(field)
    );

    const formValues = collectFormValues();

    const filteredRequiredValues = filterRequiredFields(
      formValues,
      mappingActiveRequiredFields
    );

    const payload = buildPayload(formValues, filteredRequiredValues, status);

    const requestParams = {
      entityId,
      entityName: 'PIM',
      properties: getPropertiesParams(payload),
    };

    await unmatchedRequirementMutation.mutateAsync(requestParams, {
      onSuccess: (response) => {
        if (response?.isSuccess) {
          queryClient.invalidateQueries({
            queryKey: productFullViewKeys.itemData(entityId),
          });
          invalidateMarketingAssets();
          dispatch(
            productFullViewActions.getProductImagery({
              hierarchyId: productHierarchyInfo?.productHierarchyId,
              productId: entityId,
            })
          );
          onRevalidateProduct &&
            onRevalidateProduct({
              newPayload: payload,
            });

          onUpdateUnmatchedSuccessCallback &&
            onUpdateUnmatchedSuccessCallback();

          queryClient.invalidateQueries({
            queryKey: validateActiveProductKey([entityId]),
          });

          setIsSubmitted && setIsSubmitted(true);
        }
      },
      onSettled: () => {
        setTimeout(() => {
          setIsSubmitted(false);
        }, 500);
      },
    });
  };
  const handleMakeProductActive = () => {
    dialogFunction({
      type: 'warn',
      content: <ContentConfirmRequest onClick={onModalReviewBilling} />,
      okText: 'Agree',
      onOk: confirmRequest,
    });
  };

  /**
   * Determines the correct payment action when user clicks "Confirm Request".
   * Priority order matters — checked top to bottom.
   *
   * 1. No credit card + not invoiced term → open COD modal to force user to add a card first.
   * 2. Has bypass permission (BYPASS_PRODUCT_INTERNAL_MAINTENANCE), fee is zero, or invoiced term → activate product directly, no charge needed.
   * 3. COD term (with card, with fee) → open COD modal to charge the saved card.
   * 4. Default (non-COD, non-invoiced, has fee) → show confirmation dialog before activating.
   */
  const handleConfirmClick = (handleCODCharge) => {
    if (!hasCreditCard && !isInvoicedTerm) return handleCODCharge();
    if (canBypassCharge || isInvoicedTerm) return confirmRequest();
    if (isCODTerm) return handleCODCharge();
    handleMakeProductActive();
  };

  const isDisabledNextButton = isMatched
    ? false
    : requiredErrorFields?.length > 0;
  const isDisabledBackButton = requiredErrorFields?.length === 0;

  const modalProps = {
    title: 'Update Required Values',
    width: '85%',
    closable: true,
    maskClosable: false,
    bodyStyle: {
      height: '1200px',
    },
    onCancel,
    wrapClassName: 'required-section__modal',
    ...otherProps,
  };

  return (
    <CODChargeValidating
      fee={totalFee}
      typeView={COD_PAYMENT_TYPE_VIEW.activeProduct}
      confirmRequest={confirmRequest}
      chargeItems={passedProducts}
      onCancel={onCancel}
    >
      {({ handleCODCharge }) => {
        return (
          <StyledModal
            {...modalProps}
            footer={
              <>
                {isEmptyEntityRequirement ? (
                  <Space>
                    <FormButton
                      text='Back'
                      type='secondary'
                      icon={null}
                      disabled={
                        isEmptyEntityRequirement || isDisabledBackButton
                      }
                      onClick={() => setCurrentStep(STEP_REQUIREMENT)}
                    />
                    <FormSaveButton
                      text='Confirm Request'
                      onClick={() => handleConfirmClick(handleCODCharge)}
                      disabled={!passedProducts?.length}
                      loading={activeProductChargeMutation.isLoading}
                    />
                  </Space>
                ) : (
                  <>
                    {isRequirementStep ? (
                      <Space>
                        <FormButton
                          text='Next'
                          type='secondary'
                          icon={<ArrowRightOutlined />}
                          disabled={isDisabledNextButton}
                          onClick={() => setCurrentStep(STEP_CHARGE)}
                        />
                        <FormSaveButton
                          text='Submit'
                          onClick={handleUpdateUnmatchedRequirement}
                          loading={unmatchedRequirementMutation.isLoading}
                          disabled={isMatched ? false : !isDisabledNextButton}
                        />
                      </Space>
                    ) : (
                      <Space>
                        <FormButton
                          text='Back'
                          type='secondary'
                          icon={<ArrowLeftOutlined />}
                          disabled={
                            isEmptyEntityRequirement || isDisabledBackButton
                          }
                          onClick={() => setCurrentStep(STEP_REQUIREMENT)}
                        />
                        <FormSaveButton
                          text='Confirm Request'
                          onClick={() => handleConfirmClick(handleCODCharge)}
                          disabled={!passedProducts?.length}
                          loading={activeProductChargeMutation.isLoading}
                        />
                      </Space>
                    )}
                  </>
                )}
              </>
            }
          >
            <WithLoading
              loading={unmatchedRequirementMutation.isLoading}
              tip='Validating...'
            >
              {children}
            </WithLoading>
            {visibleReviewBilling && (
              <RIVIRBillingReview
                visible={visibleReviewBilling}
                onCancel={offModalReviewBilling}
              />
            )}
          </StyledModal>
        );
      }}
    </CODChargeValidating>
  );
};

export const RequiredActiveProductSection = ({
  entityId,
  productForm,
  validateProducts,
  productImagery,
  onRevalidateProduct,
  validationTimestamp,
}) => {
  const { requiredForm, currentStep, isSubmitted, setCurrentStep } =
    useRequiredActiveProductContext();

  const { allActiveRequirements: flatRequiredProductFields } =
    useGetRequirementsForActiveProduct();

  const additionalFields = Object.keys(additionalRequirementFieldsLowercase);

  const {
    validateProducts: initialValidateProducts,
    isLoading: isLoadingErrorFields,
  } = useValidateActiveProduct({
    productIds: [entityId],
  });

  const requiredErrorFields = useMemo(() => {
    return getErrorsForActiveProduct(validateProducts?.[0]?.activeResult);
  }, [validateProducts]);

  const primaryErrors = useMemo(
    () => validateProducts?.[0]?.activeResult?.errors ?? [],
    [validateProducts]
  );

  const additionalRequirements = useMemo(
    () => validateProducts?.[0]?.activeResult?.additionalRequirements ?? {},
    [validateProducts]
  );

  const productData = useMemo(() => validateProducts?.[0], [validateProducts]);

  useSetFormValuesAndErrors({
    form: requiredForm,
    productData,
    primaryErrors,
    productForm,
  });

  const isMatched = Boolean(validateProducts?.[0]?.activeResult?.isSuccess);

  useEffect(() => {
    if (isMatched && isSubmitted) {
      setCurrentStep(STEP_CHARGE);
    }
  }, [isMatched, isSubmitted, setCurrentStep]);

  const isEmptyEntityRequirement = isEmpty(requiredErrorFields);

  const filteredEntityRequirement = useMemo(() => {
    let result = [];

    if (
      requiredErrorFields.length > 0 &&
      Object.keys(flatRequiredProductFields)?.length > 0
    ) {
      requiredErrorFields.forEach((requiredItem) => {
        const foundProperty =
          flatRequiredProductFields[`${requiredItem?.toLowerCase()}`];
        foundProperty && result.push(foundProperty);
      });
    }

    return result;
  }, [flatRequiredProductFields, requiredErrorFields]);

  const groupedRequiredProperties = useMemo(() => {
    if (filteredEntityRequirement.length === 0) return [];

    return groupPropertiesByModuleName(filteredEntityRequirement);
  }, [filteredEntityRequirement]);

  const additionalRequiredErrorFields = requiredErrorFields.filter((field) =>
    additionalFields.includes(field?.toLowerCase())
  );

  const hasBypassProductInternalPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  // get meta data for product (fee, packageLevel)
  const passedProducts = getPassedProducts(
    validateProducts,
    initialValidateProducts
  );

  const totalFee = calculateTotalFee(passedProducts);

  const canBypassCharge = hasBypassProductInternalPermission || !totalFee;

  let element = null;

  if (isEmptyEntityRequirement) {
    element = (
      <ActiveProductRequirement
        productIds={[entityId]}
        passedProducts={passedProducts}
      />
    );
  } else {
    element =
      currentStep === 0 ? (
        <Row
          style={{ width: '100%' }}
          gutter={[0, 6]}
          className='required-section'
        >
          <RequiredPropertiesForm form={requiredForm} style={{ width: '100%' }}>
            {groupedRequiredProperties?.length > 0 && (
              <RequiredPropertiesSection header='Required Properties'>
                {groupedRequiredProperties.map((groupItem) => {
                  return (
                    <Col span={24} key={groupItem.moduleDisplayName}>
                      <RequiredModuleName
                        moduleName={groupItem.moduleDisplayName}
                      />
                      {groupItem.moduleProperties.map((property) => {
                        const isError = requiredErrorFields.includes(
                          property?.fieldFullPath?.toLowerCase()
                        );

                        const error = primaryErrors.find(
                          (error) =>
                            error?.fieldName?.toLowerCase() ===
                            property?.fieldFullPath?.toLowerCase()
                        );

                        return (
                          <RequiredPropertyItem
                            key={property.fullPathDisplayName}
                            wrap={false}
                          >
                            <RequiredPropertyName
                              propertyName={property.fullPathDisplayName}
                              isError={isError}
                              isWarning={error?.isWarning}
                            />

                            {isError && (
                              <RequiredPropertyInput
                                property={property}
                                status='error'
                                primaryErrors={primaryErrors}
                                productImagery={productImagery}
                                mode='edit'
                                entityId={entityId}
                                validationTimestamp={validationTimestamp}
                              />
                            )}
                          </RequiredPropertyItem>
                        );
                      })}
                    </Col>
                  );
                })}
              </RequiredPropertiesSection>
            )}
            {additionalRequiredErrorFields?.length > 0 && (
              <RequiredPropertiesSection header='Additional Properties'>
                <AdditionalRequiredActiveProduct
                  requiredErrorFields={additionalRequiredErrorFields}
                  entityId={entityId}
                  productForm={productForm}
                  onRevalidateProduct={onRevalidateProduct}
                  additionalRequirements={additionalRequirements}
                />
              </RequiredPropertiesSection>
            )}
          </RequiredPropertiesForm>
        </Row>
      ) : (
        <ActiveProductRequirement productIds={[entityId]} />
      );
  }

  return (
    <>
      <Steps
        current={isEmptyEntityRequirement ? STEP_CHARGE : currentStep}
        style={{
          width: '80%',
          margin: 'auto',
          marginTop: 12,
        }}
      >
        <Step
          key={0}
          title={
            <Typography.Text strong style={{ fontSize: 16 }}>
              {isEmptyEntityRequirement || isMatched
                ? 'Passed Requirements'
                : 'Update Requirements'}
            </Typography.Text>
          }
          icon={
            <>
              {isEmptyEntityRequirement ? (
                <CheckCircleFilled
                  style={{
                    color: '#52C41A',
                  }}
                />
              ) : isLoadingErrorFields ? (
                <LoadingOutlined />
              ) : isMatched ? (
                <CheckCircleFilled
                  style={{
                    color: '#52C41A',
                  }}
                />
              ) : (
                <CloseCircleFilled
                  style={{
                    color: '#f5222d',
                  }}
                />
              )}
            </>
          }
        />
        <Step
          key={1}
          icon={!canBypassCharge ? <CreditCardFilled /> : <FileOutlined />}
          title={
            <Typography.Text strong style={{ fontSize: 16 }}>
              {!canBypassCharge ? 'Charge' : 'Preview'}
            </Typography.Text>
          }
        />
      </Steps>
      <div style={{ paddingTop: 24 }}>
        <WithLoading loading={isLoadingErrorFields}>{element}</WithLoading>
      </div>
    </>
  );
};

const ActiveProductRequirement = ({
  productIds,
  passedProducts: passedProductsProp,
  children,
}) => {
  const hasBypassProductInternalPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  const { passedProducts } = useValidateActiveProduct({
    productIds,
  });

  const { columns } = useIXOneColumns({ isActiveProduct: true });

  const productList = passedProductsProp || passedProducts;

  const totalFee = calculateTotalFee(productList);

  const canBypassCharge = hasBypassProductInternalPermission || !totalFee;

  const filteredColumns = filteredColumnsByFee({
    columns,
    canBypassCharge,
  });

  return (
    <>
      <IXOneHeader style={{ minHeight: 110 }} />
      <IXOneMessage message='Please confirm your selection to have the following products made Public and accessible to Retail Members of RIVIR' />
      <IXOneGrid
        columns={filteredColumns}
        productList={passedProductsProp || passedProducts}
        style={{ height: 400 }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginTop: 4,
          }}
        >
          {children}
          <ActiveProductRequirementFooter />
          {canBypassCharge ? null : <IXOneTotalFee fee={totalFee} />}
        </div>
      </IXOneGrid>
    </>
  );
};

const ActiveProductRequirementFooter = () => {
  const [visibleReviewBilling, { onModal, offModal }] = useVisibleModal();

  return (
    <div>
      <Button
        type='link'
        style={{ paddingLeft: 0, textDecoration: 'underline' }}
        onClick={onModal}
      >
        <Typography.Text
          style={{ textDecoration: 'underline', color: '#238dd9' }}
        >
          Review the RIVIR Billing T&Cs
        </Typography.Text>
      </Button>
      {visibleReviewBilling && (
        <RIVIRBillingReview
          visible={visibleReviewBilling}
          onCancel={offModal}
        />
      )}
    </div>
  );
};
